import { Box, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { UpdateBenefitActivationDTO } from 'probonio-shared-ui/api';
import { useTenant } from 'probonio-shared-ui/module/me';
import { useCallback, useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DialogLoadingButton } from '../../../component/dialog/DialogLoadingButton';
import { useBenefitContext } from '../BenefitContext';
import { ActivationDialogExtensions } from './ActivationDialog';

interface Props<T extends object> {
  activationDialogExtensions: ActivationDialogExtensions<T>;
  ChangeOptionsInfo?: React.ComponentType;
  onCancel: () => void;
  onUpdate: (update: UpdateBenefitActivationDTO) => Promise<void>;
}

interface FormValues {
  benefitOptions: unknown;
}

export const BulkEditForm = <T extends object>({
  activationDialogExtensions,
  onCancel,
  onUpdate,
  ChangeOptionsInfo,
}: Props<T>): React.JSX.Element => {
  const { t } = useTranslation('benefitsModule');
  const { tenant } = useTenant();
  const { customBenefit } = useBenefitContext();
  const queryClient = useQueryClient();

  const formMethods = useForm<FormValues>();
  const { handleSubmit, reset, control, formState } = formMethods;
  const { benefit } = useBenefitContext();
  const benefitLower = benefit?.toLowerCase();

  const { Header, ChangeOptionsFields, InlineField, onResetBenefitOptions, onUpdateActivation, hideSaveButton } =
    activationDialogExtensions;

  const handleSave = useCallback(
    async (values: FormValues) => {
      if (!onUpdateActivation) {
        throw new Error('Missing onUpdateActivation function');
      }

      await onUpdateActivation(
        {},
        values.benefitOptions as T,
        async update => {
          await onUpdate(update);
        },
        {
          tenant,
          queryClient,
        },
      );
    },
    [onUpdateActivation, tenant, queryClient, onUpdate],
  );

  useEffect(() => {
    if (onResetBenefitOptions) {
      const benefitOptions = onResetBenefitOptions(undefined, { tenant, customBenefit, queryClient });
      reset({ benefitOptions });
    }
  }, [onResetBenefitOptions, reset, tenant, customBenefit, queryClient]);

  const benefitOptions = useWatch({ control, name: 'benefitOptions' });

  const header = Header && benefitOptions ? <Header /> : null;
  const fields = ChangeOptionsFields && benefitOptions ? ChangeOptionsFields : null;
  const inlineField = InlineField && benefitOptions ? <InlineField /> : null;

  return (
    <form onSubmit={handleSubmit(handleSave)}>
      {ChangeOptionsInfo ? (
        <ChangeOptionsInfo />
      ) : (
        <Typography color="text.secondary" mb={2}>
          {t('bulkEditDialog.changeOptions.info')}
        </Typography>
      )}
      {!ChangeOptionsInfo && (
        <Typography color="text.secondary" mb={2}>
          <Trans
            i18nKey={[`benefitsModule:${benefit}.bulkEditDialog.changeOptions.info`, 'benefitsModule:bulkEditDialog.changeOptions.info']}
            components={{
              settings: <Link to={`/benefits/${benefitLower}/settings`} />,
            }}
          />
        </Typography>
      )}
      <FormProvider {...formMethods}>
        {header}
        <Box mb={1}>{inlineField}</Box>
        {fields}
      </FormProvider>
      {!hideSaveButton?.() && (
        <Stack direction="row" justifyContent="flex-end" spacing={1} mt={2}>
          <DialogLoadingButton type="submit" color="primary" loading={formState.isSubmitting}>
            {t('common:buttons.save')}
          </DialogLoadingButton>
        </Stack>
      )}
    </form>
  );
};
